import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Subscription } from '@/types'
import { api } from '@/lib/api'

interface RegisterResult {
  verificationRequired: boolean
  email?: string
}

interface AuthState {
  user: User | null
  subscription: Subscription | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  pendingVerificationEmail: string | null  // Email awaiting verification
  allowedPages: string[] | null  // Whitelisted page keys (null = not yet loaded; UI display gating)

  // Actions
  login: (email: string, password: string) => Promise<void>
  googleLogin: (idToken: string) => Promise<void>
  register: (email: string, password: string, username?: string) => Promise<RegisterResult>
  confirmEmail: (email: string, code: string) => Promise<void>
  resendVerificationCode: (email: string) => Promise<void>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
  fetchSubscription: () => Promise<void>
  fetchPageAccess: () => Promise<void>
  setUser: (user: User) => void
  clearError: () => void
  setPendingVerificationEmail: (email: string | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      subscription: null,
      isAuthenticated: !!localStorage.getItem('access_token'),
      isLoading: false,
      error: null,
      pendingVerificationEmail: null,
      allowedPages: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.login(email, password)
          const user = await api.getMe()
          const subscription = await api.getSubscription()
          set({ user, subscription, isAuthenticated: true, isLoading: false })
          get().fetchPageAccess()
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      googleLogin: async (idToken: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.googleLogin(idToken)
          const user = await api.getMe()
          const subscription = await api.getSubscription()
          set({ user, subscription, isAuthenticated: true, isLoading: false })
          get().fetchPageAccess()
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Google login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      register: async (email: string, password: string, username?: string) => {
        set({ isLoading: true, error: null })
        try {
          const result = await api.register(email, password, username)

          if (result.verification_required) {
            // Email verification required - don't authenticate yet
            set({
              isLoading: false,
              pendingVerificationEmail: result.email || email,
            })
            return { verificationRequired: true, email: result.email || email }
          }

          // Mock mode - directly authenticated
          const user = await api.getMe()
          const subscription = await api.getSubscription()
          set({ user, subscription, isAuthenticated: true, isLoading: false })
          return { verificationRequired: false }
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      confirmEmail: async (email: string, code: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.confirmEmail(email, code)
          // Email confirmed - user needs to login separately to get Cognito token
          set({
            isLoading: false,
            pendingVerificationEmail: null,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Email confirmation failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      resendVerificationCode: async (email: string) => {
        set({ isLoading: true, error: null })
        try {
          await api.resendVerificationCode(email)
          set({ isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to resend code'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      setPendingVerificationEmail: (email: string | null) => {
        set({ pendingVerificationEmail: email })
      },

      logout: async () => {
        try {
          await api.logout()
        } catch {
          // Ignore errors on logout
        }
        set({ user: null, subscription: null, isAuthenticated: false, allowedPages: null })
      },

      fetchUser: async () => {
        if (!get().isAuthenticated) return
        try {
          const user = await api.getMe()
          set({ user })
          get().fetchPageAccess()
        } catch {
          set({ user: null, isAuthenticated: false })
        }
      },

      fetchPageAccess: async () => {
        if (!get().isAuthenticated) return
        try {
          const { allowed_pages } = await api.getPageAccess()
          set({ allowedPages: allowed_pages })
        } catch {
          set({ allowedPages: [] })
        }
      },

      fetchSubscription: async () => {
        if (!get().isAuthenticated) return
        try {
          const subscription = await api.getSubscription()
          set({ subscription })
        } catch {
          // Handle error silently
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        pendingVerificationEmail: state.pendingVerificationEmail,
      }),
    }
  )
)
